import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Switch, withRouter } from 'react-router';
import { Route } from 'react-router-dom';
import URL from 'url-parse';

import { ChestInventory } from '@swf/account';
import { CommunityForumLogin } from '@swf/auth';
import CasinoGameLaunch from '@swf/components/CasinoGameLaunch/CasinoGameLaunch';
import EmbeddedCookieConsent from '@swf/components/EmbeddedCookieConsent/EmbeddedCookieConsent';
import EmbeddedFooter from '@swf/components/EmbeddedFooter/EmbeddedFooter';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import StarsFavIcon from '@swf/components/StarsFavIcon/StarsFavIcon';
import StarsModalRoutes from '@swf/components/StarsModalRoutes/StarsModalRoutes';
import StarsPage from '@swf/components/StarsPage/StarsPage';
import type { PageState } from '@swf/core/models/page';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { integrationTesting } from '@swf/core/utils';
import { KenobiApp, YodaApp } from '@swf/tools';

import DepositRoute from './components/DepositRoute';
import ManageCurrenciesRoute from './components/ManageCurrenciesRoute';
import ModalRoute from './components/ModalRoute';
import P2pTransferRoute from './components/P2pTransferRoute';
import SignupRoute from './components/SignupRoute';
import TransactionHistoryRoute from './components/TransactionHistoryRoute';
import WithdrawRoute from './components/WithdrawRoute';

type Props = RouteComponentProps<Record<string, string>> & {
  page?: PageState;
};

const Routes: React.FC<Props> = ({ location, page, staticContext = {} }) => {
  const dispatch = useDispatch<Dispatch>();
  const layout = useSelector(select.page.getLayout);
  const embedded = useSelector(select.site.isEmbedded);
  const embeddedModal = useSelector(select.location.isEmbeddedModal);
  const embeddedAccount = useSelector(select.location.isEmbeddedAccount);
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const pathname = useSelector(select.location.getPathname);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isCMSAvailable = useSelector(select.system.isCMSAvailable);
  const isRAMAvailable = useSelector(select.system.isRAMAvailable);
  const isStatic = useSelector(select.page.isStatic);
  const locale = useSelector(select.locale.get);
  const embeddedModalRoute = embeddedModal || embeddedAccount;

  useEffect(() => {
    if (!embedded && !integrationTesting) {
      const script = document.createElement('script');
      script.setAttribute('data-ot-ignore', '');
      script.src =
        '//cms.rationalcdn.com/v3/assets/blteecf9626d9a38b03/blt8873c7cca263d922/PstrkIdManager.js';
      document.head.appendChild(script);
    }
  }, [embedded]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (location.pathname !== pathname || searchParams.has('modal')) {
      dispatch.location.update(new URL(window.location.href, true));
    }
  }, [dispatch.location, layout, location, pathname]);

  if (isStatic ? !isCMSAvailable : !isCMSAvailable || !isRAMAvailable) {
    staticContext.statusCode = 503;
    return <ServiceUnavailable />;
  }

  return (
    <ErrorBoundary fallback={<ServiceUnavailable />}>
      <Helmet bodyAttributes={{ translate: 'no' }}>
        <html lang={locale} data-renderer={hasClientSession ? 'client' : 'server'} />
        <meta name="google" content="notranslate" />
      </Helmet>
      <BodyClassName className="notranslate" />
      <StarsFavIcon />
      <Switch>
        <Route path="/casino/launch/:type(game|race)/:id?" component={CasinoGameLaunch} />
        {isEmbeddedRoute && <Route path="/chest-inventory" component={ChestInventory} />}
        {isEmbeddedRoute && <Route path="/footer" component={EmbeddedFooter} />}
        {isEmbeddedRoute && <Route path="/cookie-consent" component={EmbeddedCookieConsent} />}
        <Route path="/login" render={() => <ModalRoute modal={{ path: '/auth/login' }} />} />
        <Route
          path="/redirected"
          render={() => <ModalRoute modal={{ path: '/notifications/redirected' }} />}
        />
        <Route
          path="/idscan"
          render={() =>
            isAuthenticated ? (
              <ModalRoute soft modal={{ path: '/auth/idscan' }} />
            ) : (
              <ModalRoute
                modal={{ path: '/auth/login', locationState: { path: '/auth/idscan' } }}
              />
            )
          }
        />
        <Route path="/deposit" component={DepositRoute} />
        <Route path="/withdraw" component={WithdrawRoute} />
        <Route path="/signup" component={SignupRoute} />
        <Route path="/p2p-transfer" component={P2pTransferRoute} />
        <Route path="/transaction-history" component={TransactionHistoryRoute} />
        <Route path="/manage-currencies" component={ManageCurrenciesRoute} />
        <Route path="/yoda(-next|-usmi|-uspa|-usnj)?/:siteId?/:pageId?" component={YodaApp} />
        <Route path="/kenobi-next" component={KenobiApp} />
        <Route path="/kenobi" component={KenobiApp} />
        {!embeddedModalRoute && (
          <Route
            path="/account"
            render={({ location: { pathname: path } }) => <ModalRoute modal={{ path }} />}
          />
        )}
        <Route path="/community/login" component={CommunityForumLogin} />
        <Route
          render={(props) =>
            embeddedModalRoute ? (
              <StarsModalRoutes setStyle={() => undefined} />
            ) : (
              <StarsPage page={page} {...props} />
            )
          }
        />
      </Switch>
    </ErrorBoundary>
  );
};

export default withRouter(Routes);
